<template>
    <div class="item-detail-page">
        <Loading :show="isLoading"></Loading>
        
        <!-- Success Alert Modal -->
        <transition name="modal-fade">
            <div class="modal-overlay" v-if="showAlert" @click.self="showAlert = false">
                <div class="alert-modal">
                    <div class="alert-icon">
                        <span class="glyphicon glyphicon-ok-circle"></span>
                    </div>
                    <p class="alert-message">{{ alertMsg }}</p>
                    <div class="alert-actions">
                        <router-link class="alert-btn alert-btn-primary" to="/Cart" v-if="hasLogin">
                            <span class="glyphicon glyphicon-shopping-cart"></span> {{ lang.itemDetail.cart }}
                        </router-link>
                        <router-link class="alert-btn alert-btn-primary" to="/wishList" v-if="!hasLogin">
                            <span class="glyphicon glyphicon-shopping-cart"></span> {{ lang.navi.wishList }}
                        </router-link>
                        <span class="alert-btn alert-btn-secondary" @click="showAlert = false">
                            {{ lang.itemDetail.continue }}
                        </span>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Image Slider -->
        <div class="slider-container">
            <combo-slider :slider-data="sliderData"></combo-slider>
        </div>

        <!-- Product Info Section -->
        <div class="product-section" v-if="item !== null">
            <!-- Main Product Card -->
            <div class="product-card">
                <!-- Category Badges -->
                <div class="category-badges">
                    <span class="badge badge-primary">{{ item.parentCategory.name }}</span>
                    <span class="badge badge-outline">{{ item.category.name }}</span>
                </div>

                <!-- Product Title -->
                <h1 class="product-title">{{ item.title }}</h1>

                <!-- Price & Action Row -->
                <div class="price-action-row">
                    <price-tag :item="item"></price-tag>
                    <button v-if="hasPrice" class="btn-add-to-cart" @click="showAddToCart = true">
                        <span class="glyphicon glyphicon-plus"></span>
                        {{ lang.itemDetail.select }}
                    </button>
                </div>


                <!-- Labels & Tags Section -->
                <div class="meta-section">
                    <post-label :item="item"></post-label>
                    <post-tag :item="item"></post-tag>
                    
                    <!-- Shop News -->
                    <div class="shop-news" v-if="itemPageMsgs.length > 0">
                        <div class="news-item" v-for="itemMsg in itemPageMsgs" :key="itemMsg.title">
                            <div class="news-title">{{ itemMsg.title }}</div>
                            <div class="news-content">{{ itemMsg.content }}</div>
                        </div>
                    </div>
                </div>

                <!-- Q&A Button -->
                <!-- <div class="qa-trigger">
                    <button class="btn-qa" @click="showQa = true">
                        <span class="glyphicon glyphicon-comment"></span>
                        <span>Q&A</span>
                    </button>
                </div> -->

                <!-- Q&A Modal -->
                <transition name="modal-fade">
                    <div class="modal-overlay qa-modal-overlay" v-if="showQa" @click.self="showQa = false">
                        <div class="qa-modal">
                            <div class="qa-modal-header">
                                <h3>Q&A</h3>
                                <button class="qa-close-btn" @click="showQa = false">
                                    <span class="glyphicon glyphicon-remove"></span>
                                </button>
                            </div>
                            <div class="qa-modal-body">
                                <paged-qa v-if="showQa" :to-user-id="item.userId" page-path="ItemDetail" 
                                    :page-query="item.postId" :api-client="apiClient"></paged-qa>
                            </div>
                        </div>
                    </div>
                </transition>


            </div>
            <div class="section-divider"></div>
        </div>

        <!-- Tabs Section -->
        <div class="tabs-section">
            <!-- Custom Tab Navigation -->
            <div class="tab-nav">
                <button class="tab-btn" :class="{ active: inView === 'detail' }" @click="showDetail">
                    <span class="glyphicon glyphicon-file"></span>
                    {{ lang.itemDetail.description }}
                </button>
                <button class="tab-btn" :class="{ active: inView === 'label' }" @click="showLabels">
                    <span class="glyphicon glyphicon-list-alt"></span>
                    {{ lang.itemDetail.specs }}
                </button>
                <button class="tab-btn" :class="{ active: inView === 'tag' }" @click="showTags">
                    <span class="glyphicon glyphicon-tags"></span>
                    {{ lang.itemDetail.tags }}
                </button>
            </div>

            <!-- Tab Content -->
            <div class="tab-content-wrapper">
                <transition name="tab-fade" mode="out-in">
                    <div class="tab-pane" :key="inView" :class="{ 'pre-line': inView === 'detail' }">
                        <div v-if="item !== null" class="html-content" v-html="html"></div>
                        
                        <div v-if="inView === 'detail'" class="detail-extras">
                            <!-- Videos Section -->
                            <div class="media-section" v-if="videos.length > 0">
                                <h3 class="section-title">
                                    <span class="glyphicon glyphicon-film"></span> Videos
                                </h3>
                                <div class="media-grid">
                                    <div class="media-item" :class="{ 'media-half': videos.length > 1 }"
                                        v-for="link in videos" :key="link.id">
                                        <media-link :item="link"></media-link>
                                    </div>
                                </div>
                            </div>

                            <!-- Links Section -->
                            <div class="links-section" v-if="links !== null && links.length > 0">
                                <h3 class="section-title">
                                    <span class="glyphicon glyphicon-link"></span> Links
                                </h3>
                                <div class="links-grid">
                                    <div class="link-card" 
                                        :class="{ 'link-full': links.length === 1, 'link-half': links.length > 1 }"
                                        v-for="link in links" :key="link.id">
                                        <dual-link :item="link" target="_self"></dual-link>
                                    </div>
                                </div>
                            </div>

                            <!-- Paged Infos -->
                            <div class="row">
                                <paged-infos
                                :init-filter="{ prid: postId, cardView: 'mediaStack', pageSize: 3, hidePaging: true }">
                                </paged-infos>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>

        <!-- Add to Cart Modal -->
        <modal-x :show="showAddToCart" @onclose="showAddToCart = false"
            :contentStyle="{ 'max-width': '600px', height: '80%' }" :modal-style="{ 'margin-top': '50px' }">
            <add-to-cart :post-id="item.postId" v-if="showAddToCart" @ondone="showAddToCart = false"
                :title="item.title"></add-to-cart>
        </modal-x>
    </div>
</template>

<script>
import Loading from './base/Loading.vue';
import comboSlider from './slider/Combo.vue';
import PagedQa from './PagedQA.vue';
import PagedInfos from './PagedInfos.vue';
import modalX from './base/ModalX1.vue';
import addToCart from './AddToCart.vue';
import DualLink from './links/dualLink.vue';
import mediaLink from './links/mediaLink.vue';
import PriceTag from './PriceTag.vue';
import PostTag from './PostTag.vue';
import PostLabel from './PostLabel.vue';
export default {
    components: {
        PostLabel,
        PostTag,
        PriceTag: PriceTag,
        ComboSlider: comboSlider,
        Loading: Loading,
        PagedQa: PagedQa,
        PagedInfos: PagedInfos,
        modalX: modalX,
        addToCart: addToCart,
        DualLink: DualLink,
        mediaLink: mediaLink
    },
    computed: {
        apiClient: function () {
            return this.mxnAgxApi.detailApiClient;
        },
        currency: function () {
            return this.$store.getters.configs.currency;
        },
        itemPageMsgs: function () {
            var shopNews = this.$store.getters.configs.shopNews;
            if (shopNews !== null && shopNews.length > 0) {
                return shopNews.filter((news) => { return news.msgType === "1"; });
            }
            return [];
        },
        lang: function () {
            return this.$store.getters.lang;
        },
        hasLogin: function () {
            return this.$store.getters.account !== null;
        },
        videos: function () {
            if (this.item && this.item.links !== null) {
                return this.item.links.filter((it) => { return it.linkType === 1 });
            }
            return [];
        },
        links: function () {
            if (this.item && this.item.links !== null) {
                return this.item.links.filter((it) => { return it.linkType === 0 });
            }
            return [];
        },
        hasPrice: function () {
            return (this.item && this.item.prices && this.item.prices.length > 0);
        }
    },
    props: ["postId"],
    filters: {

    },
    methods: {
        showDetail: function () {
            this.inView = "detail";
            this.html = this.item.textMsg;
        },
        showLabels: function () {
            this.inView = "label";
            var $that = this;
            $that.isLoading = true;
            this.apiClient.getPostCatLabelsPrms($that.postId).done(function (data) {
                $that.isLoading = false;
                $that.html = data.responseText;
            });
        },
        showTags: function () {
            this.inView = "tag";
            var $that = this;
            $that.isLoading = true;
            this.apiClient.getPostTagsPrms($that.postId).done(function (data) {
                $that.isLoading = false;
                $that.html = data.responseText;
            });
        },
        loadItem: function () {
            var $that = this;
            $that.isLoading = true;
            this.apiClient.getPostByIdPrms($that.postId).done(function (data) {
                $that.isLoading = false;
                $that.item = data;
                $that.html = data.textMsg;
                if (data.images !== null && data.images.length > 0) {
                    $that.sliderData = data.images.map((item, index) => {
                        return { path: item.path, link: "", msg: item.textContent };
                    });
                }
            });
        }
    },
    data: function () {
        return {
            item: null,
            html: "",
            sliderData: [],
            sliderLoaded: false,
            inView: "detail",
            isLoading: false,
            showAlert: false,
            alertMsg: "",
            showQa: false,
            showAddToCart: false
        }
    },
    mounted: function () {
        this.loadItem();
    },
    destroyed: function () {
    }

}
</script>
